import { ReactNode, Fragment } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Styles } from 'jss';
import { Theme, Colors } from '@app/theme';
import { useTrackClick } from '@app/analytics';
import { GenericButton, GenericButtonProps } from './GenericButton';
import { ThreeDotsSpinner } from '../ThreeDotsSpinner';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    ...theme.fonts.label,
    position: 'relative',
    whiteSpace: 'nowrap',
    minHeight: theme.height.regular,
    borderRadius: theme.borderRadius.std,
    '&:disabled, &[disabled]': {
      opacity: 0.5,
      cursor: 'default',
    },

    display: 'inline-flex',
    alignItems: 'stretch',
    padding: 0,
    textAlign: 'center',
  },
  buttonContent: {
    flex: 1,
    paddingRight: theme.marginBase * 3,
    paddingLeft: theme.marginBase * 3,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallButtonContent: {
    paddingRight: theme.marginBase * 2,
    paddingLeft: theme.marginBase * 2,
  },
  xsmallButtonContent: {
    padding: [[theme.marginBase / 2, theme.marginBase]],
  },
  contentLoading: {
    opacity: 0,
  },
  spinnerContainer: {
    ...theme.absoluteFill,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallButton: {
    minHeight: theme.height.small,
  },
  xsmallButton: {
    minHeight: 0,
  },
  fullMobileWidth: {
    [theme.media.mobile]: {
      width: '100%',
    },
  },
  backIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  nextIcon: {
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase,
  },
}));

const usePlainColorStyle = createUseStyles((theme: Theme) =>
  theme.colorList.reduce((acc, color) => {
    return {
      ...acc,
      [color]: {
        ...theme.plainColorCombinaisons[color],
        ...theme.plainColorsHover[color],
      },
    };
  }, {} as Styles<Colors>),
);

const useTransparentColorStyle = createUseStyles((theme: Theme) =>
  theme.colorList.reduce((acc, color) => {
    return {
      ...acc,
      [color]: {
        ...theme.lightBgHover.white,
        backgroundColor: 'transparent',
        color: theme.colorDefinitions[color].accessible,
      },
    };
  }, {} as Styles<Colors>),
);

const useMuttedColorStyle = createUseStyles((theme: Theme) =>
  theme.colorList.reduce((acc, color) => {
    return {
      ...acc,
      [color]: {
        ...theme.mutedColorCombinations[color],
        ...theme.mutedColorsHover[color],
      },
    };
  }, {} as Styles<Colors>),
);

const useWhiteBgStyle = createUseStyles((theme: Theme) => ({
  whiteBg: {
    ...theme.lightBgHover.white,
  },
}));

export type ButtonVariant = 'plain' | 'transparent' | 'muted';
export interface SpecialProps {
  color?: Colors;
  variant?: ButtonVariant;
  overGray?: boolean;
  withNextArrow?: boolean;
  withBackArrow?: boolean;
  small?: boolean;
  xsmall?: boolean;
  trackingName?: string;
  isLoading?: boolean;
  disabled?: boolean;
  text?: ReactNode;
  rightInsert?: ReactNode;
  contentClassName?: string;
  fullMobileWidth?: boolean;
}

type Props = GenericButtonProps & SpecialProps;
export type ButtonProps = Props;

export const Button = ({
  className,
  children,
  color = 'blue',
  withNextArrow,
  withBackArrow,
  small,
  xsmall,
  trackingName,
  isLoading,
  text,
  variant = 'plain',
  overGray,
  rightInsert,
  contentClassName,
  onClick,
  fullMobileWidth,
  ...props
}: Props) => {
  const classes = useStyles();
  const plainStyles = usePlainColorStyle();
  const transparentStyles = useTransparentColorStyle();
  const mutedStyles = useMuttedColorStyle();
  const whiteBgStyles = useWhiteBgStyle();
  const byVariantStyles = {
    plain: plainStyles,
    transparent: transparentStyles,
    muted: mutedStyles,
  };

  const _onClick = useTrackClick(
    (e: any) => {
      if (onClick) {
        onClick(e);
      }
    },
    [onClick],
  );

  return (
    <GenericButton
      type="button"
      {...props}
      onClick={_onClick}
      className={classnames(
        classes.button,
        byVariantStyles[variant][color],
        {
          [whiteBgStyles.whiteBg]:
            variant === 'muted' && color.startsWith('ink') && !!overGray,
          [classes.smallButton]: !!small,
          [classes.xsmallButton]: !!xsmall,
          [classes.fullMobileWidth]: !!fullMobileWidth,
        },
        className,
      )}
      data-tracking-name={trackingName}
    >
      <Fragment>
        <div
          className={classnames(classes.buttonContent, contentClassName, {
            [classes.smallButtonContent]: !!small,
            [classes.xsmallButtonContent]: !!xsmall,
            [classes.contentLoading]: !!isLoading,
          })}
        >
          {!!withBackArrow && (
            <i className={classnames('ri-arrow-left-line', classes.backIcon)} />
          )}
          {children}
          {text}
          {!!withNextArrow && (
            <i
              className={classnames('ri-arrow-right-line', classes.nextIcon)}
            />
          )}
        </div>
        {!!isLoading && (
          <div className={classes.spinnerContainer}>
            <ThreeDotsSpinner />
          </div>
        )}
        {rightInsert}
      </Fragment>
    </GenericButton>
  );
};
